import { createContext, useContext, useState, useCallback, useMemo, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── V14.76 — Pièces automatiques (inspiration Homebyme) ──────────────
const _polyCentroid = (poly) => {
  if (!poly || poly.length < 3) return null;
  let sx = 0; let sy = 0;
  for (const p of poly) { sx += (Array.isArray(p) ? p[0] : p?.x) || 0; sy += (Array.isArray(p) ? p[1] : p?.y) || 0; }
  return [sx / poly.length, sy / poly.length];
};

const _pointInPoly = (pt, poly) => {
  if (!pt || !poly || poly.length < 3) return false;
  const [px, py] = pt;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const xi = (Array.isArray(poly[i]) ? poly[i][0] : poly[i]?.x) || 0;
    const yi = (Array.isArray(poly[i]) ? poly[i][1] : poly[i]?.y) || 0;
    const xj = (Array.isArray(poly[j]) ? poly[j][0] : poly[j]?.x) || 0;
    const yj = (Array.isArray(poly[j]) ? poly[j][1] : poly[j]?.y) || 0;
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

/**
 * Réconcilie les pièces existantes avec les faces détectées par topologie.
 * • Face contenant le centroïde d'une pièce existante → la pièce est
 *   CONSERVÉE (nom/type) et son polygone mis à jour (cas « split » :
 *   l'ancienne pièce garde le côté où est son centre, l'autre côté
 *   devient une nouvelle pièce).
 * • Face sans correspondance → nouvelle pièce « Pièce N ».
 * • Pièces auto obsolètes supprimées ; pièces manuelles (source='user')
 *   non appariées conservées telles quelles.
 */
const reconcileAutoRooms = (prev, detected, newRoomIdFn) => {
  let counter = 0;
  for (const r of prev || []) {
    const m = /^pièce\s+(\d+)/i.exec(r.label || '');
    if (m) counter = Math.max(counter, parseInt(m[1], 10));
  }
  const used = new Set();
  const out = [];
  for (const d of detected || []) {
    const dPoly = d.polygon || [];
    if (dPoly.length < 3) continue;
    const dC = _polyCentroid(dPoly);
    let best = null;
    let bestDist = Infinity;
    for (const r of prev || []) {
      if (used.has(r.id)) continue;
      const c = _polyCentroid(r.polygon);
      if (!c || !_pointInPoly(c, dPoly)) continue;
      const dist = dC ? (c[0] - dC[0]) ** 2 + (c[1] - dC[1]) ** 2 : 0;
      if (dist < bestDist) { best = r; bestDist = dist; }
    }
    if (best) {
      used.add(best.id);
      out.push({ ...best, polygon: dPoly, surface_norm: d.surface_norm });
    } else {
      counter += 1;
      out.push({
        id: newRoomIdFn(),
        label: `Pièce ${counter}`,
        type: 'autre',
        polygon: dPoly,
        surface_norm: d.surface_norm,
        source: 'auto',
      });
    }
  }
  for (const r of prev || []) {
    if (!used.has(r.id) && r.source === 'user') out.push(r);
  }
  return out;
};

const _roomsFingerprint = (rooms) =>
  JSON.stringify((rooms || []).map((r) => [r.id, r.label, (r.polygon || []).length,
    +((_polyCentroid(r.polygon) || [0, 0])[0]).toFixed(4),
    +((_polyCentroid(r.polygon) || [0, 0])[1]).toFixed(4)]));

/**
 * PlanEditorContext — V14.47 Sprint 2 (single source of truth for the
 * Plan Editor V2 working state).
 *
 * Holds :
 *   • `outerPolygon`  — list of [x, y] in normalized [0..1] coordinates.
 *   • `walls`         — list of {id, x1, y1, x2, y2, type, source}
 *                       where source ∈ {'ai', 'user'} and
 *                       type ∈ {'porteur', 'cloison_fine', 'cloison_basse'}
 *   • `mode`          — 'select' | 'draw' | 'erase'
 *   • `selectedWallId`, `hoveredWallId` — bidirectional list ↔ canvas
 *                       highlight.
 *   • `isDirty`       — true when local edits haven't been persisted yet.
 *
 * Provides actions to add / update / delete walls and persists to the
 * backend through `PUT /api/projects/{id}/plan-source/structure` (debounced
 * via a 600ms timer).
 */

const PlanEditorContext = createContext(null);

export const usePlanEditor = () => {
  const ctx = useContext(PlanEditorContext);
  if (!ctx) throw new Error('usePlanEditor must be used inside <PlanEditorProvider>');
  return ctx;
};

const newWallId = () => `w_${Math.random().toString(36).slice(2, 10)}`;
const newOpeningId = () => `o_${Math.random().toString(36).slice(2, 10)}`;
const newRoomId = () => `r_${Math.random().toString(36).slice(2, 10)}`;
const newStairsId = () => `st_${Math.random().toString(36).slice(2, 10)}`;
const newSkewId = () => `sk_${Math.random().toString(36).slice(2, 10)}`;
const newFixtureId = () => `fx_${Math.random().toString(36).slice(2, 10)}`;
const newFurnitureId = () => `fn_${Math.random().toString(36).slice(2, 10)}`;

const SAVE_DEBOUNCE_MS = 600;

export const PlanEditorProvider = ({ projectId, lotId, initialStructure, children }) => {
  const [outerPolygon, setOuterPolygon] = useState(initialStructure?.outer_polygon || []);
  // Sprint 26D — Property-boundary polygon (terrain / jardin).
  const [terrainPolygon, setTerrainPolygon] = useState(initialStructure?.terrain_polygon || []);
  const [walls, setWalls] = useState(initialStructure?.walls || []);
  // V14.68.21 — History pour undo/redo sur l'édition manuelle des murs.
  // Stack de snapshots (previous walls state) empilée avant chaque
  // mutation. Trimmée à HISTORY_MAX pour éviter croissance illimitée.
  const [wallsHistory, setWallsHistory] = useState([]);
  const [wallsFuture, setWallsFuture] = useState([]);
  const HISTORY_MAX = 40;
  const [openings, setOpenings] = useState(initialStructure?.openings || []);
  const [rooms, setRooms] = useState(initialStructure?.rooms || []);
  // Sprint 26C — Stairs / staircases in the production flow.
  // {id, x, y, w, h, kind, source} where x/y/w/h are normalized [0..1]
  // and kind ∈ STAIRS_KINDS values.
  const [stairs, setStairs] = useState(initialStructure?.stairs || []);
  const [selectedStairsId, setSelectedStairsId] = useState(null);
  // V14.69 Phase 2 — Lignes de brisure (murs de mansarde) dans le flux
  // production. {id, x1, y1, x2, y2, low_side_hsp_m} normalisés [0..1].
  const [skewEdges, setSkewEdges] = useState(initialStructure?.skew_edges || []);
  const [selectedSkewId, setSelectedSkewId] = useState(null);
  // V15.08 — Éléments fixes typés (îlot, cheminée, colonne, placard, douche…)
  // {id, kind, x, y, w, h, height_m} normalisés [0..1] ; extrudés dans la maquette blanche.
  const [fixtures, setFixtures] = useState(initialStructure?.fixtures || []);
  // V15.32 — mobilier verrouillé (étape 4 · Mobilier)
  const [furniture, setFurniture] = useState(initialStructure?.furniture || []);
  const [selectedFurnitureId, setSelectedFurnitureId] = useState(null);
  const [selectedFixtureId, setSelectedFixtureId] = useState(null);
  // V14.70 — ref vers le Stage Konva (captures 📷 de fin de phase)
  const stageRef = useRef(null);
  // V14.72 — calques fantômes « superposition étages » (onion skin)
  const [ghostStructures, setGhostStructures] = useState([]);
  // V17.2 — calque d'étage (enveloppe, murs séparatifs, lots voisins, parties communes) sous le plan du lot
  const [floorOverlay, setFloorOverlay] = useState({ enabled: false, opacity: 0.65, envelope: true, walls: true, lots: true, commons: true, image: true, imageOpacity: 0.5, data: null });
  const [bgOpacity, setBgOpacity] = useState(0.85);  // V17.3 — transparence du fond (plan du lot)
  const [twoPoint, setTwoPoint] = useState({ active: false, pts: [] });  // V17.3 — calage 2 points : [src1, dst1, src2, dst2] en norm
  // Transient rect awaiting a `kind` pick from <StairsTypePicker>. Set by
  // the canvas when a click lands in 'place:stairs' mode; cleared once
  // the kind modal commits or is dismissed.
  const [stairsPending, setStairsPending] = useState(null);
  // `mode` semantic by active sub-tab :
  //   • walls   : 'select' | 'draw' | 'erase'
  //   • openings: 'select' | 'place:front_door' | 'place:interior_door' | 'place:french_door' | 'place:window' | 'place:bay_window'
  //   • rooms   : 'select' | 'erase'  (placement is via 'Détecter' only — manual drawing TBD)
  const [mode, setMode] = useState('select');
  const [selectedWallId, setSelectedWallId] = useState(null);
  const [hoveredWallId, setHoveredWallId] = useState(null);
  const [selectedOpeningId, setSelectedOpeningId] = useState(null);
  const [hoveredOpeningId, setHoveredOpeningId] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [hoveredRoomId, setHoveredRoomId] = useState(null);
  // V14.52 Sprint 7 — shared polygon-drafting state for both
  // "draw a new room" (TabPieces) and "draw outer contour" (TabStructure 2a).
  // The mode value tells the canvas where to commit the finished polygon :
  //   • mode === 'draw-room-polygon'  → addRoomPolygon
  //   • mode === 'draw-outer-polygon' → setOuterPolygonManually
  const [polygonDraft, setPolygonDraft] = useState([]);   // [[x,y], ...]
  // V14.57 Sprint 12 — Global endpoint snap toggle (Tab 2b Murs). When
  // ON, drawing a new wall or dragging an endpoint snaps to the nearest
  // existing endpoint within `endpointSnapRadius` normalized units.
  const [endpointSnap, setEndpointSnap] = useState(true);
  // V14.91 — Type de mur tracé (porteur / cloison fine / cloison basse) et
  // côté d'accrochage sur un mur existant : 'auto' (face la plus proche),
  // 'left' / 'center' / 'right' (par rapport au sens du mur cible), 'off'.
  const [drawWallType, setDrawWallType] = useState('cloison_fine');
  const [wallFaceSnap, setWallFaceSnap] = useState('auto');
  const [endpointSnapRadius, setEndpointSnapRadius] = useState(0.015);
  // V14.58 Sprint 13 LOT 3 — Menuiserie snap to walls.
  const [menuiserieSnap, setMenuiserieSnap] = useState(true);
  const [menuiserieSnapRadius, setMenuiserieSnapRadius] = useState(0.04);
  // V14.57 Sprint 12 — Canvas viewport (zoom + pan). Zoom 1.0 = fit,
  // pan {x:0, y:0} = centered. Mouse wheel zooms around pointer,
  // Ctrl+drag pans.
  const [viewport, setViewport] = useState({ zoom: 1.0, panX: 0, panY: 0 });
  const [draftHint, setDraftHint] = useState(null);       // hover position

  const pushPolygonDraftPoint = useCallback((x, y) => {
    setPolygonDraft((prev) => [...prev, [x, y]]);
  }, []);

  const cancelPolygonDraft = useCallback(() => {
    setPolygonDraft([]);
    setDraftHint(null);
  }, []);

  const [activeSubTab, setActiveSubTab] = useState(() => {
    // Sprint 26C — when the user navigated here via the mini-stack
    // (LotsSidebar → onSelectAndOpenStructure), the URL carries a
    // `subtab=walls` hint. We consume + clear it on mount so subsequent
    // refreshes don't keep snapping back to walls.
    try {
      if (typeof window !== 'undefined') {
        const url = new URL(window.location.href);
        const hint = url.searchParams.get('subtab');
        if (hint) {
          url.searchParams.delete('subtab');
          window.history.replaceState({}, '', url.toString());
          if (['contour', 'walls', 'openings'].includes(hint)) return hint;
        }
      }
    } catch { /* noop */ }
    return 'walls';
  });
  const [calibration, setCalibration] = useState(initialStructure?.calibration || null);
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSyncReport, setLastSyncReport] = useState(null);
  const [lastRoomsSyncReport, setLastRoomsSyncReport] = useState(null);
  const saveTimerRef = useRef(null);
  // V14.76 — Pièces automatiques : détection topologique après chaque
  // sauvegarde de murs (zone fermée → pièce, split → pièce modifiée + nouvelle).
  const [autoRooms, setAutoRooms] = useState(true);
  // V14.79 — Cotation automatique des murs (longueur en m, plans calibrés).
  const [showDimensions, setShowDimensions] = useState(true);
  const autoRoomsRef = useRef(true);
  useEffect(() => { autoRoomsRef.current = autoRooms; }, [autoRooms]);
  const roomsRef = useRef(rooms);
  useEffect(() => { roomsRef.current = rooms; }, [rooms]);
  const wallsRef = useRef(walls);
  useEffect(() => { wallsRef.current = walls; }, [walls]);
  const roomsSyncFnRef = useRef(null);

  // V14.78 — Défauts admin (épaisseurs murs / largeurs menuiseries en cm)
  // + dimensions de l'image de fond (nécessaires pour convertir cm → px plan).
  const [planDefaults, setPlanDefaults] = useState(null);
  const [imgDims, setImgDims] = useState(null);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/plan-defaults`, { withCredentials: true })
      .then((r) => { if (!cancelled) setPlanDefaults(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // cm → unités normalisées [0..1] (menuiseries). null si non calibré.
  const cmToNorm = useCallback((cm) => {
    const scale = calibration?.scale_m_per_norm_unit;
    if (!scale || !Number.isFinite(cm)) return null;
    return (cm / 100) / scale;
  }, [calibration]);
  // Épaisseur par défaut d'un type de mur, en px image. null si non calculable.
  const defaultWallThicknessPx = useCallback((wallType) => {
    const cm = planDefaults?.walls?.[wallType];
    const scale = calibration?.scale_m_per_norm_unit;
    if (!cm || !scale || !imgDims) return null;
    const mPerPx = scale / ((imgDims.w + imgDims.h) / 2);
    return +((cm / 100) / mPerPx).toFixed(2);
  }, [planDefaults, calibration, imgDims]);

  // The provider is keyed by `lotId` upstream so a lot switch fully
  // remounts this provider (clean state from initialStructure). No need
  // for a "reset on initialStructure change" effect — that would race
  // with the debounced save when the parent reloads after a structure
  // PUT and overwrite the user's in-flight edits.
  // V14.76 — EXCEPTION : hydratation initiale. Le fetch du saved-state par
  // lot est async : au premier montage initialStructure peut être null puis
  // arriver après coup sans remonter le provider. On hydrate UNIQUEMENT si
  // l'état est vierge (aucun mur, pas d'édition en cours) — jamais destructif.
  const hydratedRef = useRef(!!initialStructure);
  useEffect(() => {
    if (hydratedRef.current || !initialStructure) return;
    if (isDirty || saving) return;
    if ((wallsRef.current || []).length > 0) { hydratedRef.current = true; return; }
    hydratedRef.current = true;
    setOuterPolygon(initialStructure.outer_polygon || []);
    setTerrainPolygon(initialStructure.terrain_polygon || []);
    setWalls((initialStructure.walls || []).map((w) => (w.id ? w : { ...w, id: newWallId() })));
    setOpenings(initialStructure.openings || []);
    setRooms(initialStructure.rooms || []);
    setStairs(initialStructure.stairs || []);
    setSkewEdges(initialStructure.skew_edges || []);
    setFixtures(initialStructure.fixtures || []);
    setFurniture(initialStructure.furniture || []);
    setCalibration(initialStructure.calibration || null);
  }, [initialStructure, isDirty, saving]);

  // ----- Debounced save -----
  const persist = useCallback((next) => {
    if (!projectId) return;
    setSaving(true);
    const url = `${API}/projects/${projectId}/plan-source/structure${lotId ? `?lot_id=${lotId}` : ''}`;
    axios.put(url, next, { withCredentials: true })
      .then((r) => {
        setIsDirty(false);
        const sync = r.data?.materialtheque_sync;
        if (sync && (sync.created?.length || sync.error)) {
          setLastSyncReport(sync);
        }
        const roomsSync = r.data?.rooms_materialtheque_sync;
        if (roomsSync && (roomsSync.created?.length || roomsSync.error)) {
          setLastRoomsSyncReport(roomsSync);
        }
        // V14.76 — les murs viennent d'être persistés : relance la
        // détection topologique des pièces (auto-rooms).
        if (next.walls && roomsSyncFnRef.current) roomsSyncFnRef.current();
      })
      .catch(() => { /* keep dirty for next attempt */ })
      .finally(() => setSaving(false));
  }, [projectId, lotId]);

  const scheduleSave = useCallback((next) => {
    setIsDirty(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => persist(next), SAVE_DEBOUNCE_MS);
  }, [persist]);

  useEffect(() => () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); }, []);

  // ----- Actions -----

  // V14.68.21 — History pour undo/redo sur l'édition des murs.
  // Chaque mutation pushe le snapshot précédent sur wallsHistory
  // (stack limité à HISTORY_MAX) et efface wallsFuture (toute nouvelle
  // mutation invalide la forward history).
  const pushWallsHistory = useCallback((prevWalls) => {
    setWallsHistory((h) => {
      const trimmed = h.length >= HISTORY_MAX ? h.slice(1) : h;
      return [...trimmed, prevWalls];
    });
    setWallsFuture([]);
  }, [HISTORY_MAX]);

  const undoWalls = useCallback(() => {
    if (wallsHistory.length === 0) return false;
    const prev = wallsHistory[wallsHistory.length - 1];
    setWallsHistory((h) => h.slice(0, -1));
    setWallsFuture((f) => [walls, ...f]);
    setWalls(prev);
    setSelectedWallId(null);
    scheduleSave({ walls: prev, outer_polygon: outerPolygon });
    return true;
  }, [wallsHistory, walls, outerPolygon, scheduleSave]);

  const redoWalls = useCallback(() => {
    if (wallsFuture.length === 0) return false;
    const next = wallsFuture[0];
    setWallsFuture((f) => f.slice(1));
    setWallsHistory((h) => [...h, walls]);
    setWalls(next);
    setSelectedWallId(null);
    scheduleSave({ walls: next, outer_polygon: outerPolygon });
    return true;
  }, [wallsFuture, walls, outerPolygon, scheduleSave]);

  const replaceFromDetection = useCallback((detectionSegments, opts = {}) => {
    // detectionSegments = [{x1,y1,x2,y2,type}, ...] normalized [0..1]
    const newWalls = (detectionSegments || []).map((s) => ({
      id: newWallId(),
      x1: s.x1, y1: s.y1, x2: s.x2, y2: s.y2,
      type: s.type || 'cloison_fine',
      source: 'ai',
    }));
    const merged = opts.replace
      ? newWalls
      : [...walls.filter((w) => w.source !== 'ai'), ...newWalls];
    pushWallsHistory(walls);
    setWalls(merged);
    scheduleSave({ walls: merged, outer_polygon: outerPolygon });
  }, [walls, outerPolygon, scheduleSave, pushWallsHistory]);

  const setOuterFromDetection = useCallback((polygon) => {
    const poly = polygon || [];
    setOuterPolygon(poly);
    scheduleSave({ outer_polygon: poly });  // V17.1 — contour seul : pas de re-détection des pièces
  }, [scheduleSave]);

  const addWall = useCallback((x1, y1, x2, y2, wallType = drawWallType) => {
    const thickness = defaultWallThicknessPx(wallType);
    const w = { id: newWallId(), x1, y1, x2, y2, type: wallType, source: 'user',
                ...(thickness ? { thickness } : {}) };
    const next = [...walls, w];
    pushWallsHistory(walls);
    setWalls(next);
    setSelectedWallId(w.id);
    scheduleSave({ walls: next, outer_polygon: outerPolygon });
    return w.id;
  }, [walls, outerPolygon, scheduleSave, pushWallsHistory, defaultWallThicknessPx, drawWallType]);

  const updateWall = useCallback((id, patch) => {
    const next = walls.map((w) => (w.id === id ? { ...w, ...patch } : w));
    pushWallsHistory(walls);
    setWalls(next);
    scheduleSave({ walls: next, outer_polygon: outerPolygon });
  }, [walls, outerPolygon, scheduleSave, pushWallsHistory]);

  const deleteWall = useCallback((id) => {
    const next = walls.filter((w) => w.id !== id);
    pushWallsHistory(walls);
    setWalls(next);
    if (selectedWallId === id) setSelectedWallId(null);
    scheduleSave({ walls: next, outer_polygon: outerPolygon });
  }, [walls, outerPolygon, selectedWallId, scheduleSave, pushWallsHistory]);

  const clearAllWalls = useCallback(() => {
    pushWallsHistory(walls);
    setWalls([]);
    setSelectedWallId(null);
    scheduleSave({ walls: [], outer_polygon: outerPolygon });
  }, [walls, outerPolygon, scheduleSave, pushWallsHistory]);

  // V14.56 Sprint 11 — Replace all walls from server response (normalize).
  // Skips scheduleSave because the server already wrote the new state.
  // V14.68.21 — Clears history (server reload, not a user edit).
  const setWallsFromServer = useCallback((newWalls) => {
    setWalls(Array.isArray(newWalls) ? newWalls : []);
    setSelectedWallId(null);
    setWallsHistory([]);
    setWallsFuture([]);
  }, []);

  // ----- Opening actions (V14.48 Sprint 3) -----
  const addOpening = useCallback((type, x, y, opts = {}) => {
    const cfg = MENUISERIE_TYPES.find((t) => t.value === type) || MENUISERIE_TYPES[2];
    // V14.78 — largeur par défaut admin (cm) si le plan est calibré.
    const adminWidth = cmToNorm(planDefaults?.openings?.[type]);
    const o = {
      id: newOpeningId(),
      type,
      x, y,
      width: adminWidth || cfg.defaultWidth,
      height: cfg.defaultHeight,
      rotation: typeof opts.rotation === 'number' ? opts.rotation : 0,
      wall_id: opts.wall_id || null,
      source: 'user',
    };
    const next = [...openings, o];
    setOpenings(next);
    setSelectedOpeningId(o.id);
    scheduleSave({ openings: next });
    return o.id;
  }, [openings, scheduleSave, cmToNorm, planDefaults]);

  const updateOpening = useCallback((id, patch) => {
    const next = openings.map((o) => (o.id === id ? { ...o, ...patch } : o));
    setOpenings(next);
    scheduleSave({ openings: next });
  }, [openings, scheduleSave]);

  const deleteOpening = useCallback((id) => {
    const next = openings.filter((o) => o.id !== id);
    setOpenings(next);
    if (selectedOpeningId === id) setSelectedOpeningId(null);
    scheduleSave({ openings: next });
  }, [openings, selectedOpeningId, scheduleSave]);

  const clearAllOpenings = useCallback(() => {
    setOpenings([]);
    setSelectedOpeningId(null);
    scheduleSave({ openings: [] });
  }, [scheduleSave]);

  // ----- Room actions (V14.49 Sprint 4) -----
  // V15.33 — rooms déjà écrites côté serveur (fusion) : synchro locale sans re-sauvegarde.
  const setRoomsFromServer = useCallback((list) => {
    setRooms(Array.isArray(list) ? list : []);
    setSelectedRoomId(null);
  }, []);

  const replaceRoomsFromDetection = useCallback((detRooms, { keepUserRooms = true, source = 'ai' } = {}) => {
    const aiRooms = (detRooms || []).map((r) => ({
      id: newRoomId(),
      label: r.label || (r.type || 'autre').toUpperCase(),
      type: r.type || 'autre',
      polygon: r.polygon || [],
      source,
    }));
    // Replace ALL rooms with the detection. Manual rooms (source='user')
    // are preserved when present (sauf détection géométrique : remplacement total).
    const userRooms = keepUserRooms ? rooms.filter((r) => r.source === 'user') : [];
    const next = [...aiRooms, ...userRooms];
    setRooms(next);
    setSelectedRoomId(null);
    scheduleSave({ rooms: next });
  }, [rooms, scheduleSave]);

  const updateRoom = useCallback((id, patch) => {
    const next = rooms.map((r) => (r.id === id ? { ...r, ...patch } : r));
    setRooms(next);
    scheduleSave({ rooms: next });
  }, [rooms, scheduleSave]);

  const deleteRoom = useCallback((id) => {
    const next = rooms.filter((r) => r.id !== id);
    setRooms(next);
    if (selectedRoomId === id) setSelectedRoomId(null);
    scheduleSave({ rooms: next });
  }, [rooms, selectedRoomId, scheduleSave]);

  const clearAllRooms = useCallback(() => {
    setRooms([]);
    setSelectedRoomId(null);
    scheduleSave({ rooms: [] });
  }, [scheduleSave]);

  // ----- Fixture actions (V15.08) -----
  const addFixture = useCallback((kind, cx, cy) => {
    const k = fixtureKindFor(kind);
    const w = k.w, h = k.h;
    const fx = {
      id: newFixtureId(), kind: k.value,
      x: Math.max(0, Math.min(1 - w, cx - w / 2)), y: Math.max(0, Math.min(1 - h, cy - h / 2)),
      w, h, height_m: k.height_m,
    };
    const next = [...fixtures, fx];
    setFixtures(next);
    setSelectedFixtureId(fx.id);
    scheduleSave({ fixtures: next });
    return fx;
  }, [fixtures, scheduleSave]);

  const updateFixture = useCallback((id, patch) => {
    const next = fixtures.map((f) => (f.id === id ? { ...f, ...patch } : f));
    setFixtures(next);
    scheduleSave({ fixtures: next });
  }, [fixtures, scheduleSave]);

  const deleteFixture = useCallback((id) => {
    const next = fixtures.filter((f) => f.id !== id);
    setFixtures(next);
    if (selectedFixtureId === id) setSelectedFixtureId(null);
    scheduleSave({ fixtures: next });
  }, [fixtures, selectedFixtureId, scheduleSave]);

  // ----- Furniture actions (V15.32) — tailles réelles converties via l'échelle -----
  const addFurniture = useCallback((kind, cx, cy) => {
    const k = furnitureKindFor(kind);
    const S = calibration?.scale_m_per_norm_unit || 0;
    const w = S > 0 ? k.dims[0] / S : 0.08;
    const h = S > 0 ? k.dims[1] / S : 0.05;
    const fn = {
      id: newFurnitureId(), kind: k.value,
      x: Math.max(0, Math.min(1 - w, cx - w / 2)), y: Math.max(0, Math.min(1 - h, cy - h / 2)),
      w, h, rotation_deg: 0,
    };
    const next = [...furniture, fn];
    setFurniture(next);
    setSelectedFurnitureId(fn.id);
    scheduleSave({ furniture: next });
    return fn;
  }, [furniture, calibration, scheduleSave]);

  const updateFurniture = useCallback((id, patch) => {
    const next = furniture.map((f) => (f.id === id ? { ...f, ...patch } : f));
    setFurniture(next);
    scheduleSave({ furniture: next });
  }, [furniture, scheduleSave]);

  const deleteFurniture = useCallback((id) => {
    const next = furniture.filter((f) => f.id !== id);
    setFurniture(next);
    if (selectedFurnitureId === id) setSelectedFurnitureId(null);
    scheduleSave({ furniture: next });
  }, [furniture, selectedFurnitureId, scheduleSave]);

  const clearAllFurniture = useCallback(() => {
    setFurniture([]);
    setSelectedFurnitureId(null);
    scheduleSave({ furniture: [] });
  }, [scheduleSave]);

  // ----- Stair actions (Sprint 26C) -----
  // Default size for a freshly-placed stair (normalized). Stairs are
  // independently positioned (they're not anchored to a wall), so we just
  // need a rectangle big enough to read as "ESC".
  const DEFAULT_STAIR_W = 0.08;
  const DEFAULT_STAIR_H = 0.14;

  // Place a stair at (cx, cy) → opens the kind modal so the user can
  // pick Droit / Tournant / Colimaçon before it lands in the list.
  const beginStairsPlacement = useCallback((cx, cy) => {
    const x = Math.max(0, Math.min(1 - DEFAULT_STAIR_W, cx - DEFAULT_STAIR_W / 2));
    const y = Math.max(0, Math.min(1 - DEFAULT_STAIR_H, cy - DEFAULT_STAIR_H / 2));
    setStairsPending({ x, y, w: DEFAULT_STAIR_W, h: DEFAULT_STAIR_H });
  }, []);

  // Commit the pending rect with a chosen kind.
  const commitStairs = useCallback((kind) => {
    if (!stairsPending) return;
    const st = {
      id: newStairsId(),
      x: stairsPending.x, y: stairsPending.y,
      w: stairsPending.w, h: stairsPending.h,
      kind: STAIRS_KINDS.find((k) => k.value === kind) ? kind : 'droit',
      direction: 'up',
      source: 'user',
    };
    const next = [...stairs, st];
    setStairs(next);
    setStairsPending(null);
    setSelectedStairsId(st.id);
    scheduleSave({ stairs: next });
  }, [stairsPending, stairs, scheduleSave]);

  const cancelStairsPending = useCallback(() => setStairsPending(null), []);

  const updateStairs = useCallback((id, patch) => {
    const next = stairs.map((s) => (s.id === id ? { ...s, ...patch } : s));
    setStairs(next);
    scheduleSave({ stairs: next });
  }, [stairs, scheduleSave]);

  const deleteStairs = useCallback((id) => {
    const next = stairs.filter((s) => s.id !== id);
    setStairs(next);
    if (selectedStairsId === id) setSelectedStairsId(null);
    scheduleSave({ stairs: next });
  }, [stairs, selectedStairsId, scheduleSave]);

  const clearAllStairs = useCallback(() => {
    setStairs([]);
    setSelectedStairsId(null);
    scheduleSave({ stairs: [] });
  }, [scheduleSave]);

  // ----- Skew edge actions (V14.69 Phase 2 — mansardes en production) -----
  const addSkewEdge = useCallback((x1, y1, x2, y2) => {
    const se = { id: newSkewId(), x1, y1, x2, y2, low_side_hsp_m: 1.2 };
    const next = [...skewEdges, se];
    setSkewEdges(next);
    setSelectedSkewId(se.id);
    scheduleSave({ skew_edges: next });
    return se.id;
  }, [skewEdges, scheduleSave]);

  const updateSkewEdge = useCallback((id, patch) => {
    const next = skewEdges.map((s) => (s.id === id ? { ...s, ...patch } : s));
    setSkewEdges(next);
    scheduleSave({ skew_edges: next });
  }, [skewEdges, scheduleSave]);

  const deleteSkewEdge = useCallback((id) => {
    const next = skewEdges.filter((s) => s.id !== id);
    setSkewEdges(next);
    if (selectedSkewId === id) setSelectedSkewId(null);
    scheduleSave({ skew_edges: next });
  }, [skewEdges, selectedSkewId, scheduleSave]);

  // V14.52 Sprint 7 — commit the in-progress polygonDraft as a new room.
  const commitRoomPolygon = useCallback((roomType = 'autre') => {
    if (polygonDraft.length < 3) return null;
    const cfg = ROOM_TYPES.find((t) => t.value === roomType) || ROOM_TYPES[ROOM_TYPES.length - 1];
    const room = {
      id: newRoomId(),
      label: `${cfg.label} (manuel)`,
      type: roomType,
      polygon: polygonDraft,
      source: 'user',
    };
    const next = [...rooms, room];
    setRooms(next);
    setSelectedRoomId(room.id);
    setPolygonDraft([]);
    setDraftHint(null);
    scheduleSave({ rooms: next });
    return room.id;
  }, [polygonDraft, rooms, scheduleSave]);

  // V14.52 Sprint 7 — commit the in-progress polygonDraft as outer contour.
  const commitOuterPolygon = useCallback(() => {
    if (polygonDraft.length < 3) return;
    setOuterPolygon(polygonDraft);
    setPolygonDraft([]);
    setDraftHint(null);
    scheduleSave({ outer_polygon: polygonDraft });
  }, [polygonDraft, scheduleSave]);

  const clearOuterPolygon = useCallback(() => {
    setOuterPolygon([]);
    scheduleSave({ outer_polygon: [] });
  }, [scheduleSave]);

  // Sprint 26D — Terrain polygon (property boundary). Same shape as the
  // outer_polygon but rendered with a different style and stored under a
  // separate key so the building footprint stays independent.
  const commitTerrainPolygon = useCallback(() => {
    if (polygonDraft.length < 3) return;
    setTerrainPolygon(polygonDraft);
    setPolygonDraft([]);
    setDraftHint(null);
    scheduleSave({ terrain_polygon: polygonDraft });
  }, [polygonDraft, scheduleSave]);

  const clearTerrainPolygon = useCallback(() => {
    setTerrainPolygon([]);
    scheduleSave({ terrain_polygon: [] });
  }, [scheduleSave]);

  // V14.58 Sprint 13 LOT 4 — Commit a polyline of vertices as N-1 wall
  // segments. Each segment is created as an individual editable wall,
  // sharing endpoints at the chain joints (idempotent with the
  // endpoint-snap visual).
  const commitWallPolyline = useCallback((wallType = drawWallType) => {
    if (polygonDraft.length < 2) {
      setPolygonDraft([]);
      setDraftHint(null);
      return [];
    }
    const thickness = defaultWallThicknessPx(wallType);
    const newWalls = [];
    for (let i = 0; i < polygonDraft.length - 1; i += 1) {
      const [x1, y1] = polygonDraft[i];
      const [x2, y2] = polygonDraft[i + 1];
      newWalls.push({
        id: `w_user_${Date.now().toString(36)}_${i}_${Math.random().toString(36).slice(2, 6)}`,
        x1, y1, x2, y2,
        type: wallType,
        confidence: 1,
        source: 'user',
        ...(thickness ? { thickness } : {}),
      });
    }
    setWalls((prev) => [...prev, ...newWalls]);
    setPolygonDraft([]);
    setDraftHint(null);
    scheduleSave({ walls: [...walls, ...newWalls] });
    return newWalls;
  }, [polygonDraft, walls, scheduleSave, defaultWallThicknessPx, drawWallType]);

  // ----- Calibration (V14.50 Sprint 5) -----
  const setCalibrationFromTwoPoints = useCallback((p1, p2, distance_m) => {
    const cal = { p1, p2, distance_m };
    setCalibration({
      ...cal,
      scale_m_per_norm_unit:
        distance_m /
        Math.max(1e-6, Math.sqrt((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2)),
    });
    scheduleSave({ calibration: cal });
  }, [scheduleSave]);

  const clearCalibration = useCallback(() => {
    setCalibration(null);
    scheduleSave({ calibration: null });
  }, [scheduleSave]);

  // V14.76 — Détection auto des pièces après persist des murs.
  const syncRoomsFromTopology = useCallback(async () => {
    if (!autoRoomsRef.current || !projectId) return;
    if ((wallsRef.current || []).length < 3) return;
    try {
      const url = `${API}/projects/${projectId}/plan-source/structure/detect-rooms-from-topology${lotId ? `?lot_id=${lotId}` : ''}`;
      const r = await axios.post(url, { replace_existing: false }, { withCredentials: true });
      const detected = r.data?.rooms || [];
      const prev = roomsRef.current || [];
      const next = reconcileAutoRooms(prev, detected, newRoomId);
      if (_roomsFingerprint(prev) !== _roomsFingerprint(next)) {
        setRooms(next);
        scheduleSave({ rooms: next });
        const added = next.length - prev.length;
        if (added > 0) {
          toast.info(`${added} pièce(s) détectée(s)`, {
            description: 'Zone fermée → pièce auto. Nommez-la dans l\'onglet 3 · Pièces.',
          });
        }
      }
    } catch { /* silencieux — la détection auto ne doit jamais bloquer l'édition */ }
  }, [projectId, lotId, scheduleSave]);
  useEffect(() => { roomsSyncFnRef.current = syncRoomsFromTopology; }, [syncRoomsFromTopology]);

  // V14.78 — Duplication depuis un autre étage : murs porteurs + escaliers +
  // menuiseries extérieures + contour. Coordonnées copiées telles quelles
  // (normalisées 0-1) → superposition verticale parfaite par construction.
  const duplicateFromFloor = useCallback((src, opts = {}) => {
    const { porteurs = true, withStairs = true, extOpenings = true, withOuter = true } = opts;
    const idMap = {};
    const addedWalls = [];
    if (porteurs) {
      (src.walls || []).filter((w) => w.type === 'porteur').forEach((w) => {
        const nid = newWallId();
        if (w.id) idMap[w.id] = nid;
        addedWalls.push({ ...w, id: nid, source: 'dup_floor' });
      });
    }
    const mergedWalls = [...walls, ...addedWalls];

    let mergedOpenings = openings;
    let addedOpenings = 0;
    if (extOpenings) {
      const EXT = new Set(['front_door', 'window', 'bay_window', 'french_door']);
      const add = (src.openings || [])
        .filter((o) => EXT.has(o.type))
        .map((o) => ({
          ...o,
          id: `op_${Math.random().toString(36).slice(2, 10)}`,
          wall_id: o.wall_id ? (idMap[o.wall_id] || null) : null,
        }));
      addedOpenings = add.length;
      mergedOpenings = [...openings, ...add];
    }

    let mergedStairs = stairs;
    let addedStairs = 0;
    if (withStairs) {
      const add = (src.stairs || []).map((s) => ({
        ...s, id: `st_${Math.random().toString(36).slice(2, 10)}`,
      }));
      addedStairs = add.length;
      mergedStairs = [...stairs, ...add];
    }

    const nextOuter = (withOuter && (!outerPolygon || outerPolygon.length < 3) && (src.outer_polygon || []).length >= 3)
      ? src.outer_polygon
      : outerPolygon;

    pushWallsHistory(walls);
    setWalls(mergedWalls);
    setOpenings(mergedOpenings);
    setStairs(mergedStairs);
    setOuterPolygon(nextOuter);
    scheduleSave({
      walls: mergedWalls, openings: mergedOpenings,
      stairs: mergedStairs, outer_polygon: nextOuter,
    });
    return { walls: addedWalls.length, openings: addedOpenings, stairs: addedStairs };
  }, [walls, openings, stairs, outerPolygon, scheduleSave, pushWallsHistory]);

  const value = useMemo(() => ({
    outerPolygon,
    terrainPolygon,
    walls,
    openings,
    rooms,
    autoRooms, setAutoRooms,
    showDimensions, setShowDimensions,
    planDefaults, imgDims, setImgDims, cmToNorm, defaultWallThicknessPx,
    stairs,
    selectedStairsId, setSelectedStairsId,
    stairsPending,
    duplicateFromFloor,
    skewEdges,
    selectedSkewId, setSelectedSkewId,
    fixtures,
    selectedFixtureId, setSelectedFixtureId,
    furniture,
    selectedFurnitureId, setSelectedFurnitureId,
    stageRef,
    ghostStructures, setGhostStructures,
    floorOverlay, setFloorOverlay,
    bgOpacity, setBgOpacity,
    twoPoint, setTwoPoint,
    calibration,
    polygonDraft, draftHint, setDraftHint,
    mode, setMode,
    activeSubTab, setActiveSubTab,
    selectedWallId, setSelectedWallId,
    hoveredWallId, setHoveredWallId,
    selectedOpeningId, setSelectedOpeningId,
    hoveredOpeningId, setHoveredOpeningId,
    selectedRoomId, setSelectedRoomId,
    hoveredRoomId, setHoveredRoomId,
    endpointSnap, setEndpointSnap,
    drawWallType, setDrawWallType,
    wallFaceSnap, setWallFaceSnap,
    endpointSnapRadius, setEndpointSnapRadius,
    menuiserieSnap, setMenuiserieSnap,
    menuiserieSnapRadius, setMenuiserieSnapRadius,
    viewport, setViewport,
    isDirty, saving, lastSyncReport, lastRoomsSyncReport,
    // V14.68.21 — Undo/redo state pour l'édition des murs
    canUndoWalls: wallsHistory.length > 0,
    canRedoWalls: wallsFuture.length > 0,
    wallsHistoryDepth: wallsHistory.length,
    actions: {
      replaceFromDetection,
      setOuterFromDetection,
      addWall, updateWall, deleteWall, clearAllWalls, setWallsFromServer,
      undoWalls, redoWalls,
      addOpening, updateOpening, deleteOpening, clearAllOpenings,
      replaceRoomsFromDetection, setRoomsFromServer, updateRoom, deleteRoom, clearAllRooms,
      beginStairsPlacement, commitStairs, cancelStairsPending,
      updateStairs, deleteStairs, clearAllStairs,
      addSkewEdge, updateSkewEdge, deleteSkewEdge,
      addFixture, updateFixture, deleteFixture,
      addFurniture, updateFurniture, deleteFurniture, clearAllFurniture,
      setCalibrationFromTwoPoints, clearCalibration,
      pushPolygonDraftPoint, cancelPolygonDraft,
      commitRoomPolygon, commitOuterPolygon, clearOuterPolygon,
      commitTerrainPolygon, clearTerrainPolygon, commitWallPolyline,
    },
  }), [
    outerPolygon, walls, openings, rooms, calibration,
    terrainPolygon, autoRooms, showDimensions,
    planDefaults, imgDims, cmToNorm, defaultWallThicknessPx,
    stairs, selectedStairsId, stairsPending,
    duplicateFromFloor,
    skewEdges, selectedSkewId,
    ghostStructures, floorOverlay, bgOpacity, twoPoint,
    polygonDraft, draftHint,
    mode, activeSubTab,
    selectedWallId, hoveredWallId, selectedOpeningId, hoveredOpeningId,
    selectedRoomId, hoveredRoomId,
    endpointSnap, endpointSnapRadius, viewport, drawWallType, wallFaceSnap,
    menuiserieSnap, menuiserieSnapRadius,
    isDirty, saving, lastSyncReport, lastRoomsSyncReport,
    wallsHistory, wallsFuture,
    replaceFromDetection, setOuterFromDetection,
    addWall, updateWall, deleteWall, clearAllWalls, setWallsFromServer,
    undoWalls, redoWalls,
    addOpening, updateOpening, deleteOpening, clearAllOpenings,
    replaceRoomsFromDetection, setRoomsFromServer, updateRoom, deleteRoom, clearAllRooms,
    beginStairsPlacement, commitStairs, cancelStairsPending,
    updateStairs, deleteStairs, clearAllStairs,
    addSkewEdge, updateSkewEdge, deleteSkewEdge,
    fixtures, selectedFixtureId, addFixture, updateFixture, deleteFixture,
    furniture, selectedFurnitureId, addFurniture, updateFurniture, deleteFurniture, clearAllFurniture,
    setCalibrationFromTwoPoints, clearCalibration,
    pushPolygonDraftPoint, cancelPolygonDraft,
    commitRoomPolygon, commitOuterPolygon, clearOuterPolygon, commitWallPolyline,
    commitTerrainPolygon, clearTerrainPolygon,
  ]);

  return (
    <PlanEditorContext.Provider value={value}>
      {children}
    </PlanEditorContext.Provider>
  );
};

export const WALL_TYPES = [
  { value: 'porteur',       label: 'Mur porteur',       color: '#06b6d4', // cyan
    description: 'Mur structurel épais', strokeWidth: 4 },
  { value: 'cloison_fine',  label: 'Cloison fine',      color: '#ef4444', // red
    description: 'Cloison standard de séparation', strokeWidth: 2 },
  { value: 'cloison_basse', label: 'Cloison basse',     color: '#a855f7', // purple
    description: 'Séparation visuelle partielle', strokeWidth: 2 },
  // V14.96 — Limite virtuelle : découpe les pièces ouvertes (cuisine ouverte…)
  // sans aucun mur rendu (ni masque ControlNet, ni 3D).
  { value: 'mur_invisible', label: 'Mur invisible',     color: '#22c55e', // green
    description: 'Limite virtuelle de pièce ouverte — aucun mur rendu', strokeWidth: 1.5, dashed: true, virtual: true },
];

export const isVirtualWall = (w) => (w?.type || w?.kind) === 'mur_invisible';

export const wallStyleFor = (type) => WALL_TYPES.find((t) => t.value === type) || WALL_TYPES[1];

// V14.48 Sprint 3 — Menuiseries (doors/windows) typed library.
// `defaultWidth` / `defaultHeight` are normalized [0..1] sizes of the
// rotated rectangle drawn on the Konva canvas. The `subrole` maps to
// the Matériauthèque sub-role used by the bidirectional sync.
export const MENUISERIE_TYPES = [
  { value: 'front_door',     label: "Porte d'entrée",     short: 'PE',  color: '#10b981', // emerald
    defaultWidth: 0.09, defaultHeight: 0.022, subrole: 'porte_entree',
    description: "Porte palière vers l'extérieur" },
  { value: 'interior_door',  label: 'Porte intérieure',   short: 'PI',  color: '#f59e0b', // amber
    defaultWidth: 0.08, defaultHeight: 0.018, subrole: 'portes_interieures',
    description: 'Porte battante entre deux pièces' },
  { value: 'closet_door',    label: 'Porte de placard',   short: 'PP',  color: '#ec4899', // pink
    defaultWidth: 0.06, defaultHeight: 0.015, subrole: 'portes_placard',
    description: 'Porte de placard coulissante / battante intégrée' },
  { value: 'french_door',    label: 'Porte-fenêtre',      short: 'PF',  color: '#14b8a6', // teal
    defaultWidth: 0.12, defaultHeight: 0.022, subrole: 'portes_fenetres',
    description: 'Porte vitrée donnant sur balcon / terrasse / jardin' },
  { value: 'window',         label: 'Fenêtre classique',  short: 'F',   color: '#3b82f6', // blue
    defaultWidth: 0.10, defaultHeight: 0.018, subrole: 'fenetres',
    description: 'Fenêtre standard à double vantail' },
  { value: 'bay_window',     label: 'Baie vitrée',        short: 'BV',  color: '#8b5cf6', // violet
    defaultWidth: 0.22, defaultHeight: 0.022, subrole: 'fenetres',
    description: 'Baie vitrée pleine hauteur' },
];

export const openingStyleFor = (type) =>
  MENUISERIE_TYPES.find((t) => t.value === type) || MENUISERIE_TYPES[2];

// V15.30 — Menuiseries paramétrables (miroir de backend/opening_specs.py).
export const OPENING_MODES = [
  { value: 'coulissant', label: 'Coulissant' },
  { value: 'battant', label: 'Battant' },
  { value: 'fixe', label: 'Fixe' },
  { value: 'galandage', label: 'À galandage' },
  { value: 'oscillo_battant', label: 'Oscillo-battant' },
];
export const OPENING_SPEC_DEFAULTS = {
  bay_window:    { leaves: 2, height_m: 2.15, sill_m: 0,    mode: 'coulissant' },
  french_door:   { leaves: 2, height_m: 2.15, sill_m: 0,    mode: 'battant' },
  window:        { leaves: 2, height_m: 1.25, sill_m: 0.90, mode: 'battant' },
  front_door:    { leaves: 1, height_m: 2.10, sill_m: 0,    mode: 'battant' },
  interior_door: { leaves: 1, height_m: 2.10, sill_m: 0,    mode: 'battant' },
  closet_door:   { leaves: 2, height_m: 2.10, sill_m: 0,    mode: 'coulissant' },
};
export const GLAZED_OPENING_TYPES = ['bay_window', 'french_door', 'window'];
export const openingSpecFor = (o, widthM = 0) => {
  const d = OPENING_SPEC_DEFAULTS[o?.type] || OPENING_SPEC_DEFAULTS.window;
  const autoLeaves = o?.type === 'bay_window' && widthM > 0 ? Math.max(2, Math.min(6, Math.round(widthM / 1.1))) : d.leaves;
  return {
    leaves: Number.isFinite(o?.leaves) ? o.leaves : autoLeaves,
    height_m: Number.isFinite(o?.height_m) ? o.height_m : d.height_m,
    sill_m: Number.isFinite(o?.sill_m) ? o.sill_m : d.sill_m,
    mode: o?.mode || d.mode,
    isDefault: !(Number.isFinite(o?.leaves) || Number.isFinite(o?.height_m) || Number.isFinite(o?.sill_m) || o?.mode),
  };
};

// V14.49 Sprint 4 — Rooms (pièces) typed library + sec/humide flag.
// The `subroles` array drives the bidirectional Matériauthèque sync :
// each room of a given type forces those finish-subroles to exist as
// material slots in the project's matériauthèque.
export const ROOM_TYPES = [
  { value: 'sejour',    label: 'Séjour',         wet: false, outdoor: false, color: '#fbbf24', // amber
    subroles: ['sols_sec', 'peinture_murale'] },
  { value: 'chambre',   label: 'Chambre',        wet: false, outdoor: false, color: '#fb923c', // orange-light
    subroles: ['sols_sec', 'peinture_murale'] },
  { value: 'cuisine',   label: 'Cuisine',        wet: true,  outdoor: false, color: '#06b6d4', // cyan
    subroles: ['sols_humide', 'credence_cuisine', 'peinture_murale'] },
  { value: 'sdb',       label: 'Salle de bain',  wet: true,  outdoor: false, color: '#0ea5e9', // sky
    subroles: ['sols_humide', 'faience_murale'] },
  { value: 'wc',        label: 'WC',             wet: true,  outdoor: false, color: '#3b82f6', // blue
    subroles: ['sols_humide', 'faience_murale'] },
  { value: 'couloir',   label: 'Couloir',        wet: false, outdoor: false, color: '#a78bfa', // violet
    subroles: ['sols_sec', 'peinture_murale'] },
  { value: 'entree',    label: 'Entrée',         wet: false, outdoor: false, color: '#d97706', // amber-600
    subroles: ['sols_sec', 'peinture_murale'] },
  { value: 'buanderie', label: 'Buanderie',      wet: true,  outdoor: false, color: '#2dd4bf', // teal-400
    subroles: ['sols_humide', 'peinture_murale'] },
  { value: 'terrasse',  label: 'Terrasse',       wet: false, outdoor: true,  color: '#84cc16', // lime-500
    subroles: ['sols_exterieur'], ext: { rail_style: 'muret', rail_height_m: 0.45, covered: false } },
  { value: 'balcon',    label: 'Balcon',         wet: false, outdoor: true,  color: '#a3e635', // lime-400
    subroles: ['sols_exterieur'], ext: { rail_style: 'vitre', rail_height_m: 1.05, covered: false } },
  { value: 'loggia',    label: 'Loggia',         wet: false, outdoor: true,  color: '#65a30d', // lime-600
    subroles: ['sols_exterieur', 'peinture_murale'], ext: { rail_style: 'barreaude', rail_height_m: 1.0, covered: true } },
  { value: 'patio',     label: 'Patio',          wet: false, outdoor: true,  color: '#4d7c0f', // lime-700
    subroles: ['sols_exterieur'], ext: { rail_style: 'aucun', rail_height_m: 0, covered: false } },
  { value: 'jardin',    label: 'Jardin / cour',  wet: false, outdoor: true,  color: '#22c55e', // green-500
    subroles: [], ext: { rail_style: 'haie', rail_height_m: 1.2, covered: false } },
  { value: 'autre',     label: 'Autre',          wet: false, outdoor: false, color: '#9ca3af', // gray
    subroles: [] },
];

// V15.18 — styles de bordure des extérieurs (maquette blanche)
export const RAIL_STYLES = [
  { value: 'vitre',     label: 'Garde-corps vitré' },
  { value: 'barreaude', label: 'Garde-corps barreaudé' },
  { value: 'plein',     label: 'Garde-corps plein' },
  { value: 'muret',     label: 'Muret bas' },
  { value: 'haie',      label: 'Haie / clôture' },
  { value: 'aucun',     label: 'Aucune bordure' },
];

export const roomStyleFor = (type) =>
  ROOM_TYPES.find((t) => t.value === type) || ROOM_TYPES[ROOM_TYPES.length - 1];

// Sprint 26C — Stairs / Trémies. Three kinds matching the Lab editor :
//   • droit      : straight flight (1 volée)
//   • tournant   : L or U-shape (quart / demi-tour)
//   • colimacon  : spiral / hélicoïdal
// Each kind ships a description for the picker modal and an svg-glyph
// hint that StructureCanvas uses to render the inside of the rectangle.
export const STAIRS_KINDS = [
  { value: 'droit',      label: 'Droit',
    description: '1 volée rectiligne — orientation libre' },
  { value: 'tournant',   label: 'Tournant',
    description: 'Quart-tournant ou demi-tour (en L / en U)' },
  { value: 'colimacon',  label: 'Colimaçon',
    description: 'Hélicoïdal — emprise circulaire' },
];

export const stairsKindFor = (kind) =>
  STAIRS_KINDS.find((k) => k.value === kind) || STAIRS_KINDS[0];

// V14.69 Phase 1 — Liaison verticale de l'escalier.
export const STAIRS_DIRECTIONS = [
  { value: 'up',   label: 'Monte',    glyph: '↑' },
  { value: 'down', label: 'Descend',  glyph: '↓' },
  { value: 'both', label: 'Les deux', glyph: '↕' },
];

export const stairsDirectionFor = (d) =>
  STAIRS_DIRECTIONS.find((k) => k.value === d) || STAIRS_DIRECTIONS[0];

// V15.08 — Éléments fixes : tailles par défaut (normalisées) et hauteur (m ; 0 = jusqu'au plafond).
export const FIXTURE_KINDS = [
  { value: 'ilot',         label: 'Îlot central',      w: 0.10, h: 0.05, height_m: 0.90, color: '#f59e0b' },
  { value: 'plan_travail', label: 'Plan de travail',   w: 0.12, h: 0.04, height_m: 0.90, color: '#fbbf24' },
  { value: 'cheminee',     label: 'Cheminée',          w: 0.06, h: 0.03, height_m: 0,    color: '#ef4444' },
  { value: 'colonne',      label: 'Colonne / poteau',  w: 0.025, h: 0.025, height_m: 0,  color: '#94a3b8' },
  { value: 'placard',      label: 'Placard / dressing', w: 0.08, h: 0.035, height_m: 0,  color: '#a78bfa' },
  { value: 'douche',       label: 'Douche',            w: 0.06, h: 0.06, height_m: 2.00, color: '#38bdf8' },
  { value: 'baignoire',    label: 'Baignoire',         w: 0.10, h: 0.05, height_m: 0.55, color: '#22d3ee' },
  // V15.34 — demandés par l'utilisateur
  { value: 'douche_italienne', label: 'Douche à l’italienne (paroi verte)', w: 0.075, h: 0.05, height_m: 2.00, color: '#34d399' },
  { value: 'wc',           label: 'WC',                w: 0.025, h: 0.04, height_m: 0.42, color: '#7dd3fc' },
  { value: 'meuble_vasque', label: 'Meuble-vasque',    w: 0.05, h: 0.03, height_m: 0.85, color: '#67e8f9' },
  { value: 'frigo',        label: 'Réfrigérateur',     w: 0.04, h: 0.04, height_m: 1.90, color: '#fcd34d' },
];

export const fixtureKindFor = (kind) =>
  FIXTURE_KINDS.find((k) => k.value === kind) || FIXTURE_KINDS[0];

// V15.32 — Mobilier verrouillé : dimensions réelles L × P × H (m), couleur du gabarit
// (miroir de backend/furniture_catalog.py). `group` = rubrique du sélecteur.
export const FURNITURE_KINDS = [
  { value: 'canape_3p',    label: 'Canapé 3 places',           dims: [2.20, 0.95, 0.85], color: '#3b82f6', group: 'Séjour' },
  { value: 'canape_angle', label: "Canapé d'angle",            dims: [2.80, 1.80, 0.85], color: '#2563eb', group: 'Séjour' },
  { value: 'fauteuil',     label: 'Fauteuil',                  dims: [0.85, 0.85, 0.80], color: '#60a5fa', group: 'Séjour' },
  { value: 'table_basse',  label: 'Table basse',               dims: [1.20, 0.60, 0.40], color: '#93c5fd', group: 'Séjour' },
  { value: 'meuble_tv',    label: 'Meuble TV',                 dims: [1.80, 0.45, 0.50], color: '#1e40af', group: 'Séjour' },
  { value: 'tapis',        label: 'Tapis',                     dims: [2.40, 1.70, 0.02], color: '#78716c', group: 'Séjour' },
  { value: 'table_repas',  label: 'Table à manger + chaises',  dims: [2.20, 1.60, 0.75], color: '#10b981', group: 'Repas' },
  { value: 'table_ronde',  label: 'Table ronde + chaises',     dims: [1.60, 1.60, 0.75], color: '#059669', group: 'Repas' },
  { value: 'lit_90',       label: 'Lit 90',                    dims: [0.90, 1.90, 0.50], color: '#ec4899', group: 'Chambre' },
  { value: 'lit_140',      label: 'Lit 140',                   dims: [1.40, 1.90, 0.50], color: '#db2777', group: 'Chambre' },
  { value: 'lit_160',      label: 'Lit 160',                   dims: [1.60, 2.00, 0.50], color: '#be185d', group: 'Chambre' },
  { value: 'lit_180',      label: 'Lit 180',                   dims: [1.80, 2.00, 0.50], color: '#9d174d', group: 'Chambre' },
  { value: 'chevet',       label: 'Table de chevet',           dims: [0.45, 0.40, 0.55], color: '#f472b6', group: 'Chambre' },
  { value: 'armoire',      label: 'Armoire',                   dims: [1.80, 0.60, 2.20], color: '#a855f7', group: 'Rangement' },
  { value: 'commode',      label: 'Commode',                   dims: [1.20, 0.50, 0.85], color: '#c084fc', group: 'Rangement' },
  { value: 'bibliotheque', label: 'Bibliothèque',              dims: [1.00, 0.35, 2.00], color: '#d97706', group: 'Rangement' },
  { value: 'bureau',       label: 'Bureau + chaise',           dims: [1.40, 0.70, 0.75], color: '#f59e0b', group: 'Bureau' },
  { value: 'salon_jardin', label: 'Salon de jardin',           dims: [1.80, 1.80, 0.75], color: '#84cc16', group: 'Extérieur' },
  { value: 'bain_soleil',  label: 'Bain de soleil',            dims: [2.00, 0.70, 0.35], color: '#a3e635', group: 'Extérieur' },
];

export const furnitureKindFor = (kind) =>
  FURNITURE_KINDS.find((k) => k.value === kind) || FURNITURE_KINDS[0];
